"use client";

import Link from "@/components/MyLink";
import cx from "classnames";
import Image from "next/image";
import { useParams } from "next/navigation";
import {
  type ReactNode,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  MdOutlineCheckBox,
  MdOutlineCheckBoxOutlineBlank,
  MdPlayArrow,
} from "react-icons/md";
import shallow from "zustand/shallow";

import ChordDiagram from "@/components/ChordDiagram";
import { LayoutPane } from "@/components/Layout";
import LoadingIndicator from "@/components/LoadingIndicator";
import SongViewAutoPlay from "@/components/SongViewAutoPlay";
import SongViewDrawer from "@/components/SongViewDrawer";
import { useAppStore } from "@/store/app";
import {
  useReplicachePreferences,
  useReplicacheSongs,
} from "@/store/replicache";
import {
  type ChordProBlock,
  type ChordProPart,
  type ParsedLine,
  durationToSeconds,
  parseChordProString,
} from "@/utils/chordProParse";
import { chordColors } from "@/utils/constants";
import formatSecondsAsMinutesSeconds from "@/utils/formatSecondsAsMinutesSeconds";
import { getSongPlayRoute } from "@/utils/getSongRoute";
import useInfo from "@/utils/info";
import titleCase from "@/utils/titleCase";
import type { Instrument, Song, SongId } from "@/utils/types";

const LOG_TAG = "SongView";
const DEBUG = false;

const estimateSongDuration = (chordPro: string): number => {
  // Rough estimate: 3 seconds per line of content
  const lines = chordPro
    .split("\n")
    .filter(
      (line) => line.trim() && !line.startsWith("{") && !line.startsWith("#"),
    );
  return Math.max(lines.length * 3, 30); // minimum 30 seconds
};

export default function SongView({ song: _songProp }: { song?: Song }) {
  const { preferences } = useReplicachePreferences();

  useEffect(() => {
    if (DEBUG) {
      console.debug(LOG_TAG, "render", { preferences });
      throw new Error("TESTING !234");
    }
  });

  const { activeChord } = useAppStore(
    ({ activeChord }) => ({
      activeChord,
    }),
    shallow,
  );

  const { isRouteSongEditor, isRouteSongPlay, isRouteSongView } = useInfo();

  const { songs } = useReplicacheSongs();
  const { songId } = useParams() as { songId: SongId };
  //const pathName = usePathname();
  //console.debug(LOG_TAG, "render", { songId, pathName });
  const song = songs?.[songId] ? songs[songId] : _songProp;

  const [chordProParsedRefCurrent, setChordProParsedNode] =
    useState<HTMLDivElement | null>();
  const chordProParsedRef = (node: HTMLDivElement | null) => {
    if (node !== null) {
      setChordProParsedNode(node);
    }
  };

  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(true);
  const [doTranspose, setDoTranspose] = useState(false);

  const [chordProParsed, setSongChordPro] = useState(
    song ? parseChordProString(song.chordPro) : null,
  );

  useEffect(() => {
    if (!song) return;
    //console.debug("TRANSPOSE?", doTranspose, song.capo);
    const chordProParsed = parseChordProString(
      song.chordPro,
      doTranspose && song.capo ? song.capo : 0,
    );
    setSongChordPro(chordProParsed);
  }, [doTranspose, song]);

  useEffect(() => {
    if (activeChord) {
      setIsDrawerCollapsed(false);
    } else {
      setIsDrawerCollapsed(true);
    }
  }, [activeChord]);

  const layoutClassName = isRouteSongEditor ? "flex-1" : undefined;

  if (!song || !chordProParsed) {
    return (
      <LayoutPane className={layoutClassName}>
        <LoadingIndicator />
      </LayoutPane>
    );
  }

  const renderAutoPlay = !!(isRouteSongPlay && chordProParsedRefCurrent);

  const songDuration = chordProParsed.duration
    ? durationToSeconds(chordProParsed.duration)
    : estimateSongDuration(song.chordPro);
  const showSongPlayIcon = !!(
    isRouteSongView &&
    isDrawerCollapsed &&
    chordProParsed &&
    songDuration
  );

  return (
    <LayoutPane className={layoutClassName}>
      {renderAutoPlay && (
        <SongViewAutoPlay
          durationSeconds={songDuration}
          scrollElement={chordProParsedRefCurrent}
        />
      )}
      <div className="flex flex-col h-full relative print:h-auto print:text-primary-content print:pl-24">
        <div
          ref={chordProParsedRef}
          className={cx(
            "flex-1 overflow-x-hidden overflow-y-auto px-6",
            isRouteSongPlay
              ? "text-xl pt-[calc(50vh-0.5rem)] pb-[calc(50vh-1.25rem)]"
              : "pt-4 pb-16", // Add bottom padding to ensure content doesn't get hidden behind drawer
          )}
        >
          <div
            className={cx(
              "overflow-x-auto mb-2 print:hidden",
              isRouteSongEditor ? "flex" : "hidden",
            )}
          >
            {chordProParsed.chords.map((chord: string, _index: number) => (
              <ChordDiagram
                key={`chord-diagram-${chord}`}
                chord={chord}
                instrument={preferences?.x_instrument as Instrument}
              />
            ))}
          </div>

          <SongProperties
            isRouteSongPlay={isRouteSongPlay}
            song={song}
            toggleTranspose={
              <span
                className="ml-6 text-base flex items-center align-middle"
                onClick={() => setDoTranspose(!doTranspose)}
              >
                <span className="btn-xs text-2xl btn-ghost text-accent-content pr-[2px]">
                  {doTranspose ? (
                    <MdOutlineCheckBox />
                  ) : (
                    <MdOutlineCheckBoxOutlineBlank />
                  )}
                </span>
                <span>Transpose</span>
              </span>
            }
          />

          {song.audioFileUrl && !isRouteSongPlay && (
            <audio
              src={song.audioFileUrl}
              controls
              className="mt-4 mb-6 print:hidden"
            />
          )}

          {chordProParsed.parts.map((part: ChordProPart, i: number) => (
            <Section
              key={part.url ? part.url : `part-url-${i}`}
              part={part}
              isRouteSongPlay={isRouteSongPlay}
            />
          ))}
        </div>

        {showSongPlayIcon && (
          <Link
            className="btn btn-circle btn-ghost shadow-lg bg-primary hover:bg-primary-focus fixed z-[1] right-4 bottom-[60px] text-2xl text-white"
            href={getSongPlayRoute({ song })}
          >
            <MdPlayArrow />
          </Link>
        )}
        {isRouteSongView && chordProParsed && (
          <div className="flex-shrink-0">
            {" "}
            {/* Add this wrapper */}
            <SongViewDrawer
              chords={chordProParsed.chords}
              instrument={preferences?.x_instrument as Instrument}
              isCollapsed={isDrawerCollapsed}
              setIsCollapsed={setIsDrawerCollapsed}
            />
          </div>
        )}
      </div>
    </LayoutPane>
  );
}

const Section = ({
  part,
  isRouteSongPlay,
}: {
  part: ChordProPart;
  isRouteSongPlay: boolean;
}) => {
  if (DEBUG) console.debug(LOG_TAG, "Section type", part.type, { part });
  let content: ReactNode;
  switch (part.type) {
    case "verse":
    case "chorus":
      content = (
        <ChordsAndLyrics part={part} isRouteSongPlay={isRouteSongPlay} />
      );
      break;
    case "image":
      content = <XImage part={part} />;
      break;
    case "carriage-return":
      content = <div className="h-4" />;
      break;
    case "comment":
    case "choruscomment": {
      const block = part.linesParsed[0] as ChordProBlock;
      content = <div className="pb-2">{block.text}</div>;
      break;
    }
    case "tab":
      content = (
        <div className="pt-2 mb-2 font-mono">
          {part.linesParsed.map((line: ParsedLine) => {
            const tabBlock = line as ChordProBlock;
            return <div key={`line-${line}`}>{tabBlock.text}</div>;
          })}
        </div>
      );
      break;
    case "x_url":
      content = (
        <a href={part.url} target="_blank" rel="noopener noreferrer">
          {part.title || part.url}
        </a>
      );
      break;
    case "x_video":
      content = <Video part={part} isRouteSongPlay={isRouteSongPlay} />;
      break;
    case "x_audio":
      content = <Audio part={part} key={part.url} />;
      break;
    case "x_pdf":
      content = <PDF part={part} />;
      break;
    default:
      console.warn("No implementation yet for part.type", part.type, part);
  }
  return <div>{content}</div>;
};

const Audio = ({ part }: { part: ChordProPart }) => {
  const { isRouteSongPlay } = useInfo();
  const audioRef = useRef(null);

  if (part.url?.includes("songbook.studio")) {
    return (
      <iframe
        width="100%"
        height="300"
        scrolling="no"
        frameBorder="no"
        src={part.url}
        title={part.url}
      />
    );
  }

  return (
    // biome-ignore lint/a11y/useMediaCaption: <explanation>
    <audio
      ref={audioRef}
      controls
      style={{ width: "90%" }}
      preload="auto"
      autoPlay={isRouteSongPlay}
      className="mt-2 mb-4 print:hidden"
    >
      <source src={part.url} />
    </audio>
  );
};

const Video = ({
  part,
  isRouteSongPlay,
}: {
  part: ChordProPart;
  isRouteSongPlay: boolean;
}) => {
  if (!part.url) {
    return null;
  }
  if (part.url.indexOf("youtube") !== -1) {
    return (
      <iframe
        className={cx(
          "mt-2 mb-4 w-full min-h-[315px]",
          isRouteSongPlay && "fixed w-[300px] h-50 min-h-0 top-0 right-[70px]",
        )}
        title={part.url}
        src={part.url}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen
      />
    );
  }
  if (part.url.indexOf("soundcloud") !== -1) {
    return (
      <iframe
        title={part.url}
        width="100%"
        height="300"
        scrolling="no"
        frameBorder="no"
        src={part.url}
      />
    );
  }
  // biome-ignore lint/a11y/useMediaCaption: <explanation>
  return <video src={part.url} controls style={{ width: "80%" }} />;
};

const PDF = ({ part }: { part: ChordProPart }) => {
  const windowSize = useAppStore(({ windowSize }) => windowSize);
  const [height, setHeight] = useState<number>(400);
  const bodyHeight = windowSize.height - 150;
  useEffect(() => {
    setHeight(bodyHeight);
  }, [bodyHeight]);

  if (!part.url) {
    return null;
  }
  return (
    <div>
      <object
        data={`${part.url}#view=Fit`}
        type="application/pdf"
        height={`${height}px`}
        width="100%"
      >
        <p>
          You do not have a PDF plugin, but you can{" "}
          <a href={part.url} target="_blank" rel="noopener noreferrer">
            download the PDF file.
          </a>
        </p>
      </object>
    </div>
  );
};

const XImage = ({ part }: { part: ChordProPart }) => {
  console.debug("XImage", { part });
  if (!part.url) {
    return null;
  }
  const { height, title, url, width } = part;
  return (
    <div
      style={{
        width: width ? `${width}px` : "100%",
        height: height ? `${height}px` : undefined,
      }}
    >
      <Image src={url} alt={title || ""} />
    </div>
  );
};

const ChordsAndLyrics = ({
  part,
  isRouteSongPlay,
}: {
  part: ChordProPart;
  isRouteSongPlay: boolean;
}) => {
  //console.debug(LOG_TAG, "ChordsAndLyrics", { part });
  return (
    <div className="mb-8 pt-2 print:break-inside-avoid">
      {part.linesParsed.map((parsed: ParsedLine, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          key={`parsed-line-chords-lyrics-${i}`}
          className={cx(
            "mb-4 leading-loose text-accent-content print:text-black",
          )}
        >
          {(parsed as ChordProBlock[]).map((chunk, j, chunks) => {
            if (chunk.type === "text") {
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              return <Word key={`word-${j}`} word={chunk} />;
            }
            if (chunk.type === "space") {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <span key={j} className="inline-block min-w-[0.4rem]">
                  {" "}
                </span>
              );
            }
            if (chunk.type === "chord") {
              let nextIsChord = false;
              if (chunks[j + 1] && chunks[j + 1].type === "chord") {
                nextIsChord = true;
              } else if (
                chunks[j + 1] &&
                chunks[j + 1].type === "space" &&
                chunks[j + 2] &&
                chunks[j + 2].type === "chord"
              ) {
                nextIsChord = true;
              }
              return (
                <Chord
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  key={j}
                  chord={chunk}
                  nextIsChord={nextIsChord}
                  isRouteSongPlay={isRouteSongPlay}
                />
              );
            }
            throw new Error(`Found chunk with unknown type: ${chunk.type}`);
          })}
        </div>
      ))}
    </div>
  );
};

const Word = ({ word }: { word: ChordProBlock }) => {
  return <span className="leading-none">{word.text}</span>;
};

const Chord = ({
  chord,
  nextIsChord,
  isRouteSongPlay,
}: {
  chord: ChordProBlock;
  nextIsChord: boolean;
  isRouteSongPlay: boolean;
}) => {
  const [, startTransition] = useTransition();
  const { activeChord, setActiveChord } = useAppStore(
    ({ activeChord, setActiveChord }) => ({
      activeChord,
      setActiveChord,
    }),
    shallow,
  );
  const [selected, setSelected] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setSelected(false);
  }, [activeChord]);

  const onClick = () => {
    startTransition(() => {
      setActiveChord(chord.text || "");
      setTimeout(() => setSelected(true));
    });
  };

  const color = chord.text ? chordColors[chord.text.substr(0, 1)] : undefined;

  return (
    <span className="font-medium relative text-base" onClick={onClick}>
      <span
        className={cx(
          "absolute leading-none cursor-pointer preventDrawerClose print:text-black print:text-sm print:font-light",
          isRouteSongPlay ? "top-[-1.2rem]" : "top-[-1rem]",
        )}
        style={{
          color: selected ? "bg-base-100" : color,
          backgroundColor: selected ? color : undefined,
        }}
      >
        {chord.text}
      </span>
      {nextIsChord && <span className="invisible">{chord.text}</span>}
    </span>
  );
};

const SongProperties = ({
  isRouteSongPlay,
  song,
  toggleTranspose,
}: {
  isRouteSongPlay: boolean;
  song: Song;
  toggleTranspose: ReactNode;
}) => {
  // const songProperties: PartialButNoUndefined<ChordProDirectives[]> = [
  //   "title",
  //   "artist",
  //   "subtitle",
  //   "composer",
  //   "key",
  //   "tempo",
  //   "time",
  //   "capo",
  //   "duration",
  // ];
  const songProperties = [
    "title",
    "artist",
    "subtitle",
    "composer",
    "key",
    "capo",
    "duration",
    "tempo",
    "time",
  ];
  // console.debug(LOG_TAG, "SongProperties", {
  //   chordProParsed,
  //   songProperties,
  //   song,
  // });

  // For `duration` and `capo` we want to use the typed values we store on
  // the song object directly, not the parsed values from the chordPro string.
  return (
    <div className="song-properties text-base-content print:text-black">
      {songProperties.map(
        (property) =>
          !!song[property as keyof Song] && (
            <div
              key={`song-property-${property}`}
              className={cx(
                "mb-1 last:mb-8 flex items-center direction-row",
                property === "title"
                  ? "text-xl font-medium hidden print:block"
                  : property === "subtitle" || property === "artist"
                    ? "text-lg font-normal last:mb-4 hidden print:block"
                    : "text-base",
                isRouteSongPlay &&
                  ["title", "subtitle", "artist"].includes(property) &&
                  "block text-lg",
                isRouteSongPlay &&
                  (property === "title" ? "text-primary" : "hidden"),
              )}
            >
              {["title", "artist", "subtitle"].indexOf(property) === -1 && (
                <span>{`${titleCase(property)}: `}</span>
              )}
              <span className="ml-2">
                {property === "capo"
                  ? song[property as keyof Song]
                  : property === "duration"
                    ? formatSecondsAsMinutesSeconds(song.duration)
                    : (song[property as keyof Song] as string)}
              </span>

              {property === "capo" && toggleTranspose}
            </div>
          ),
      )}
    </div>
  );
};
